import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { useApi } from '@/lib/api-context';
import type { StageKind } from '@/lib/stage-kind';

import {
  applicableResponseSchema,
  standardFor,
  standardProgress,
} from '../time/standard-presentation';
import { skeletonLine } from '@/components/ui/skeleton';

// "Estándar de la etapa" bajo los relojes (spec 2026-10-14 §7): el trabajo de toda la
// etapa contra el estándar vigente al inicio de este trabajo. Informa; no bloquea nada.

interface Props {
  flowInstanceId: string;
  companyId: string | null;
  stageKind: StageKind;
  /** Esfuerzo de toda la etapa: todas las personas y todos los intentos, sin esperas ni pausas. */
  effortSeconds: number;
}

export function StageStandard({
  flowInstanceId,
  companyId,
  stageKind,
  effortSeconds,
}: Props): ReactElement {
  const api = useApi();
  const query = useQuery({
    queryKey: ['stage-standard', companyId, flowInstanceId],
    queryFn: async () => {
      const raw = await api.get<unknown>(
        `/api/v1/quality/production-standards/applicable?flowInstanceId=${flowInstanceId}`,
      );
      return applicableResponseSchema.parse(raw).data;
    },
  });

  if (query.isPending) {
    return (
      <div className="stage-std" aria-hidden="true">
        <span className={skeletonLine({ height: 'sm' })} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="stage-std" role="alert">
        <p className="soft">No se pudo leer el estándar de la etapa.</p>
        <button type="button" className="btn mut sm" onClick={() => void query.refetch()}>
          Reintentar
        </button>
      </div>
    );
  }

  const minutes = standardFor(query.data, stageKind);
  if (minutes === null) {
    return (
      <div className="stage-std">
        <p className="soft">Esta etapa no tiene estándar definido para este tipo de documento.</p>
      </div>
    );
  }

  const p = standardProgress(effortSeconds, minutes);
  return (
    <div className="stage-std">
      <div className="stage-std-head">
        <span className="cl">Estándar de la etapa</span>
        <span className="dim-mono">{p.text}</span>
      </div>
      <div className="rowb">
        <div
          className={cn('meter', p.over ? 'danger' : 'ok')}
          role="progressbar"
          aria-label="Trabajo de la etapa contra su estándar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={p.barPct}
          aria-valuetext={`${p.pct} %, ${p.text}`}
        >
          <i style={{ width: `${p.barPct}%` }} />
        </div>
        <span className="dim-mono">{p.pct} %</span>
      </div>
      {p.overText !== null ? <p className="stage-std-over">{p.overText}</p> : null}
    </div>
  );
}
